import employeeQualificationService from '../../../services/EmployeeQualificationService';
import { format, isValid, parseISO, startOfDay } from 'date-fns';

export async function saveQualificationInlineChanges(row, changes) {
  if (Object.prototype.hasOwnProperty.call(changes, 'employeeMandatoryQualifications')) {
    if (!row.employeeId) throw new Error('Employee id is required to update mandatory qualifications');
    const mandatory = changes.employeeMandatoryQualifications;
    if (typeof mandatory !== 'boolean') throw new Error('Mandatory qualification must be true or false');
    const existing = Array.isArray(row.employeeMandatoryQualifications) ? row.employeeMandatoryQualifications : [];
    const mandatoryQualifications = mandatory
      ? existing.includes(row.name)
        ? existing
        : [...existing, row.name]
      : existing.filter((name) => name !== row.name);
    const response = await employeeQualificationService.updateEmployeeMandatoryQualifications({
      employeeId: row.employeeId,
      mandatoryQualifications
    });
    const savedMandatoryQualifications = response?.data?.data?.mandatoryQualifications;
    if (!Array.isArray(savedMandatoryQualifications)) {
      throw new Error('The mandatory qualification update could not be confirmed');
    }
    const wasSaved = savedMandatoryQualifications.includes(row.name) === mandatory;
    if (!wasSaved) {
      throw new Error('The server returned a different mandatory qualification value');
    }
    return { employeeMandatoryQualifications: savedMandatoryQualifications };
  }

  const updated = { ...row, ...changes };
  const qualificationData = {
    employeeKeyPayId: updated.employeeKeyPayId,
    qualificationId: updated.qualificationId,
    name: updated.name,
    expiryDate: updated.expiryDate || null,
    issueDate: updated.issueDate || null,
    referenceNumber: updated.referenceNumber,
    documentId: updated.documentId || null,
    status: 'accepted'
  };
  const today = startOfDay(new Date());
  for (const field of ['expiryDate', 'issueDate']) {
    if (!changes[field]) continue;
    // Date inputs represent local calendar days, not UTC timestamps.
    const date = typeof changes[field] === 'string' ? parseISO(changes[field]) : new Date(changes[field]);
    if (!isValid(date)) throw new Error('Please enter a valid date');
    const day = startOfDay(date);
    if (field === 'issueDate' && day > today) {
      throw new Error('Issue date must be today or in the past');
    }
    if (field === 'expiryDate' && day < today) {
      throw new Error('Expiry date must be today or in the future');
    }
  }
  // Inline editing saves one cell at a time; unrelated empty fields must not
  // block the save. Only the identifiers needed to locate the record are required.
  if (!qualificationData.employeeKeyPayId || !qualificationData.qualificationId || !qualificationData.name) {
    throw new Error('Employee KeyPay id, qualification id and name are required to identify the qualification');
  }
  if (typeof updated.qualificationUrl === 'string' && updated.qualificationUrl.trim()) {
    qualificationData.qualificationUrl = updated.qualificationUrl;
  }
  // eslint-disable-next-line no-undef
  const formData = new FormData();
  // eslint-disable-next-line no-undef
  formData.append('userId', localStorage.getItem('userId'));
  formData.append('qualificationData', JSON.stringify(qualificationData));
  const response = await employeeQualificationService.updateEmployeeQualificationInQboByAdmin(formData);
  const saved = response?.data?.data || {};
  for (const field of ['issueDate', 'expiryDate']) {
    if (!Object.prototype.hasOwnProperty.call(changes, field) || !Object.prototype.hasOwnProperty.call(saved, field))
      continue;
    const calendarDate = (value) => {
      if (!value) return null;
      const date = typeof value === 'string' ? parseISO(value) : new Date(value);
      return isValid(date) ? format(date, 'yyyy-MM-dd') : value;
    };
    if (calendarDate(saved[field]) !== calendarDate(changes[field])) {
      throw new Error(
        `The server returned a different ${
          field === 'issueDate' ? 'issue' : 'expiry'
        } date. The update could not be confirmed. Please refresh and try again.`
      );
    }
  }
  return {
    expiryDate: saved.expiryDate ?? qualificationData.expiryDate,
    issueDate: saved.issueDate ?? qualificationData.issueDate,
    referenceNumber: saved.referenceNumber ?? qualificationData.referenceNumber,
    status: qualificationData.status
  };
}
